import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { collaboratorAuthHeader } from "./useCollaborator";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Per-photo comment thread + gallery-wide summary (Phase G3).
 *
 *   thread     : Map<photo_id, Comment[]>     — cached threads (reopening a
 *                photo's drawer is instant; we revalidate in background).
 *   summary    : { [photo_id]: count }        — drives grid badges.
 *   postComment(photoId, body, parentId?)
 *   editComment(photoId, commentId, body)
 *   deleteComment(photoId, commentId)
 *   loadThread(photoId)                       — force-revalidate (called on
 *                                                drawer open).
 *
 * All mutators are optimistic with rollback on error so the composer feels
 * instant. We deliberately don't websocket-push other clients' updates yet
 * (Phase G5 work) — instead we poll the open thread every 12s.
 */
export default function useGalleryComments({ slug, token, enabled }) {
  const [threads, setThreads] = useState({});
  const [summary, setSummary] = useState({});
  const [loadingPhotoId, setLoadingPhotoId] = useState(null);

  const headers = collaboratorAuthHeader(token);

  const loadSummary = useCallback(async () => {
    if (!enabled || !token) { setSummary({}); return; }
    try {
      const { data } = await axios.get(`${API}/galleries/${slug}/comments/summary`, { headers });
      const m = {};
      for (const row of (data.photos || [])) m[row.photo_id] = row.count;
      setSummary(m);
    } catch {
      // Silent — summary is decorative
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, enabled]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadThread = useCallback(async (photoId) => {
    if (!enabled || !token || !photoId) return;
    setLoadingPhotoId(photoId);
    try {
      const { data } = await axios.get(`${API}/galleries/${slug}/photos/${photoId}/comments`, { headers });
      setThreads((t) => ({ ...t, [photoId]: data.comments || [] }));
    } catch {
      // Leave any cached thread in place
    } finally {
      setLoadingPhotoId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, enabled]);

  const postComment = useCallback(async (photoId, body, parentId = null, identity = {}) => {
    const tempId = `optimistic-${Date.now()}-${Math.random()}`;
    const optimistic = {
      id: tempId,
      photo_id: photoId,
      parent_comment_id: parentId,
      author_collaborator_id: identity.collaboratorId || "me",
      author_display_name: identity.displayName || "You",
      author_role: identity.role || "collaborator",
      body,
      created_at: new Date().toISOString(),
      edited_at: null,
      deleted: false,
      _optimistic: true,
    };
    setThreads((t) => ({ ...t, [photoId]: [...(t[photoId] || []), optimistic] }));
    setSummary((s) => ({ ...s, [photoId]: (s[photoId] || 0) + 1 }));
    try {
      const { data } = await axios.post(
        `${API}/galleries/${slug}/photos/${photoId}/comments`,
        { body, parent_comment_id: parentId },
        { headers },
      );
      // Replace optimistic with the real row
      setThreads((t) => ({
        ...t,
        [photoId]: (t[photoId] || []).map((c) => c.id === tempId ? data.comment : c),
      }));
      return { ok: true, comment: data.comment };
    } catch (err) {
      // Rollback
      setThreads((t) => ({
        ...t,
        [photoId]: (t[photoId] || []).filter((c) => c.id !== tempId),
      }));
      setSummary((s) => ({ ...s, [photoId]: Math.max(0, (s[photoId] || 1) - 1) }));
      return { ok: false, error: err };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token]);

  const editComment = useCallback(async (photoId, commentId, body) => {
    const prevList = threads[photoId] || [];
    const prevRow = prevList.find((c) => c.id === commentId);
    if (!prevRow) return { ok: false };
    const optimistic = { ...prevRow, body, edited_at: new Date().toISOString() };
    setThreads((t) => ({
      ...t,
      [photoId]: (t[photoId] || []).map((c) => c.id === commentId ? optimistic : c),
    }));
    try {
      const { data } = await axios.patch(
        `${API}/galleries/${slug}/photos/${photoId}/comments/${commentId}`,
        { body },
        { headers },
      );
      setThreads((t) => ({
        ...t,
        [photoId]: (t[photoId] || []).map((c) => c.id === commentId ? data.comment : c),
      }));
      return { ok: true };
    } catch (err) {
      setThreads((t) => ({
        ...t,
        [photoId]: (t[photoId] || []).map((c) => c.id === commentId ? prevRow : c),
      }));
      return { ok: false, error: err };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, threads]);

  const deleteComment = useCallback(async (photoId, commentId) => {
    const prevList = threads[photoId] || [];
    const prevRow = prevList.find((c) => c.id === commentId);
    if (!prevRow) return { ok: false };
    setThreads((t) => ({
      ...t,
      [photoId]: (t[photoId] || []).map((c) => c.id === commentId ? { ...c, deleted: true, body: "" } : c),
    }));
    setSummary((s) => ({ ...s, [photoId]: Math.max(0, (s[photoId] || 1) - 1) }));
    try {
      await axios.delete(`${API}/galleries/${slug}/photos/${photoId}/comments/${commentId}`, { headers });
      return { ok: true };
    } catch (err) {
      // Rollback
      setThreads((t) => ({
        ...t,
        [photoId]: (t[photoId] || []).map((c) => c.id === commentId ? prevRow : c),
      }));
      setSummary((s) => ({ ...s, [photoId]: (s[photoId] || 0) + 1 }));
      return { ok: false, error: err };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, threads]);

  return {
    threads,
    summary,
    loadingPhotoId,
    loadThread,
    postComment,
    editComment,
    deleteComment,
    refreshSummary: loadSummary,
  };
}
